import { useState, FormEvent, ChangeEvent } from 'react';
import { ProyectoMock } from '../../../mocks/proyectos';
import { useAuth } from '../../../hooks/useAuth';

interface Props {
  onSubmitSuccess?: () => void;
  onAddProyecto?: (p: ProyectoMock) => void;
}

interface Autor {
  id: number;
  nombre: string;
  afiliacion: string;
  orcid: string;
}

const generateProjectCode = (): string => {
  const year = new Date().getFullYear();
  const num = String(Math.floor(Math.random() * 900) + 100);
  return `PRY-${year}-${num}`;
};

let autorCounter = 2;

const CARRERAS_POR_SEDE: Record<string, string[]> = {
  Oruro: [
    'Administración Estratégica de Empresas',
    'Bioquímica y Farmacia',
    'Derecho',
    'Enfermería',
    'Fisioterapia y Kinesiología',
    'Medicina',
    'Ingeniería Comercial',
    'Ingeniería Financiera',
    'Odontología',
    'Psicología',
  ],
  Cochabamba: [
    'Administración Estratégica de Empresas',
    'Bioquímica y Farmacia',
    'Comunicación Social',
    'Derecho',
    'Medicina',
    'Ingeniería Comercial',
    'Ingeniería Financiera',
    'Odontología',
    'Psicología',
    'Enfermería',
    'Fisioterapia y Kinesiología',
  ],
};

export default function FormRecepcion({ onSubmitSuccess, onAddProyecto }: Props) {
  const { currentUser } = useAuth();
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [projectCode, setProjectCode] = useState('');
  const [charCount, setCharCount] = useState({ resumen: 0, diseno: 0, cronograma: 0, publicacion: 0 });
  const [objetivoGeneral, setObjetivoGeneral] = useState('');
  const [objetivosEspecificos, setObjetivosEspecificos] = useState<string[]>(['']);
  const [objGeneralOpen, setObjGeneralOpen] = useState(true);
  const [objEspecificosOpen, setObjEspecificosOpen] = useState(true);
  const [autores, setAutores] = useState<Autor[]>([
    { id: 1, nombre: '', afiliacion: '', orcid: '' },
  ]);
  const [sede, setSede] = useState('');
  const [carrera, setCarrera] = useState('');
  const countWords = (value: string) => value.trim() ? value.trim().split(/\s+/).length : 0;

  const updateChar = (field: keyof typeof charCount, value: string) => {
    setCharCount(prev => ({ ...prev, [field]: value.length }));
  };

  const handleResumenChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const words = e.target.value.trim() ? e.target.value.trim().split(/\s+/) : [];

    if (words.length > 500) {
      const limitedValue = words.slice(0, 500).join(' ');
      e.target.value = limitedValue;
      setCharCount(prev => ({ ...prev, resumen: 500 }));
      return;
    }

    setCharCount(prev => ({ ...prev, resumen: countWords(e.target.value) }));
  };

  const addObjetivoEspecifico = () => {
    setObjetivosEspecificos(prev => [...prev, '']);
  };

  const removeObjetivoEspecifico = (idx: number) => {
    setObjetivosEspecificos(prev => prev.filter((_, i) => i !== idx));
  };

  const updateObjetivoEspecifico = (idx: number, value: string) => {
    setObjetivosEspecificos(prev => prev.map((v, i) => i === idx ? value : v));
  };

  const addAutor = () => {
    setAutores(prev => [...prev, { id: autorCounter++, nombre: '', afiliacion: '', orcid: '' }]);
  };

  const removeAutor = (id: number) => {
    if (autores.length === 1) return;
    setAutores(prev => prev.filter(a => a.id !== id));
  };

  const updateAutor = (id: number, field: keyof Omit<Autor, 'id'>, value: string) => {
    setAutores(prev => prev.map(a => a.id === id ? { ...a, [field]: value } : a));
  };

  const formatOrcid = (value: string) => {
    const digits = value.replace(/[^0-9X]/gi, '').toUpperCase();
    const parts = [];
    for (let i = 0; i < digits.length && i < 16; i += 4) {
      parts.push(digits.slice(i, i + 4));
    }
    return parts.join('-');
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    const form = e.currentTarget;
    const fd = new FormData(form);
    const get = (name: string) => (fd.get(name) as string) || '';
    const data = new URLSearchParams();
    const code = generateProjectCode();
    data.append('codigo_proyecto', code);

    const elements = form.elements as HTMLFormControlsCollection;
    Array.from(elements).forEach((el) => {
      const input = el as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
      if (input.name) {
        if (input.type === 'checkbox') {
          data.append(input.name, (input as HTMLInputElement).checked ? 'Sí' : 'No');
        } else if (input.type !== 'radio' && input.type !== 'file') {
          data.append(input.name, input.value);
        }
      }
    });

    ['necesidad_etica'].forEach(name => {
      const val = get(name);
      if (val) data.set(name, val);
    });

    const autoresStr = autores
      .map((a, i) => `Autor ${i + 1}: ${a.nombre} | ${a.afiliacion}${a.orcid ? ` | ORCID: https://orcid.org/${a.orcid}` : ''}`)
      .join('\n');
    data.set('equipo', autoresStr);

    const especificosStr = objetivosEspecificos
      .filter(o => o.trim())
      .map((o, i) => `OE${i + 1}: ${o}`)
      .join('\n');
    const objetivosStr = `Objetivo General: ${objetivoGeneral}${especificosStr ? `\n${especificosStr}` : ''}`;
    data.set('objetivos', objetivosStr);

    try {
      await fetch('https://readdy.ai/api/form/d6sq8jah642up23iq240', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });

      const eticaVal = get('necesidad_etica');
      const unidadVal = sede && carrera ? `${sede} — ${carrera}` : (sede || carrera || '');
      const newProject: ProyectoMock = {
        codigo: code,
        titulo: get('titulo'),
        investigador: autores[0]?.nombre || get('nombre_investigador'),
        email: get('email'),
        area: get('area_conocimiento'),
        categoria: 'Proyecto institucional',
        estado: 'Pendiente evaluación',
        decision: '—',
        etica: eticaVal.toLowerCase().startsWith('sí') ? 'Sí' : (eticaVal.toLowerCase().startsWith('si') ? 'Sí' : 'No'),
        porcentaje: 0,
        fechaRecepcion: new Date().toISOString().split('T')[0],
        fechaDecision: '—',
        presupuesto: get('presupuesto') ? `Bs. ${get('presupuesto')}` : '—',
        unidad: unidadVal,
        telefono: get('telefono'),
        resumen: get('resumen'),
        objetivos: `Objetivo General: ${objetivoGeneral}${objetivosEspecificos.filter(o => o.trim()).length ? '\n' + objetivosEspecificos.filter(o => o.trim()).map((o, i) => `OE${i + 1}: ${o}`).join('\n') : ''}`,
        diseno: get('diseno'),
        cronograma: get('cronograma'),
        equipo: autoresStr,
        financiamiento: get('financiamiento'),
        necesidad_etica: eticaVal,
        justificacion_etica: get('justificacion_etica'),
        plan_publicacion: get('plan_publicacion'),
        fecha_inicio: get('fecha_inicio'),
      };
      onAddProyecto?.(newProject);

      setProjectCode(code);
      setSubmitted(true);
      onSubmitSuccess?.();
    } catch {
      // silenciar error
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="w-20 h-20 flex items-center justify-center bg-teal-50 rounded-full mb-5">
          <i className="ri-checkbox-circle-fill text-4xl text-teal-600" />
        </div>
        <h2 className="text-2xl font-bold text-slate-800 mb-2">¡Proyecto enviado correctamente!</h2>
        <p className="text-slate-500 mb-6 max-w-md text-sm">
          Su propuesta ha sido recibida por la UGPP. A continuación encontrará el código asignado a su proyecto.
          Guárdelo para el seguimiento y la subida de documentos.
        </p>

        <div className="bg-teal-50 border-2 border-teal-400 rounded-xl px-10 py-6 mb-8 w-full max-w-sm">
          <p className="text-xs font-semibold text-teal-600 uppercase tracking-widest mb-2">Código de su proyecto</p>
          <p className="text-4xl font-extrabold text-teal-700 tracking-wider font-mono">{projectCode}</p>
          <p className="text-xs text-slate-500 mt-3">
            Conserve este código. Lo necesitará para subir documentos y realizar consultas.
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 w-full max-w-sm">
          <button
            onClick={() => onSubmitSuccess?.()}
            className="whitespace-nowrap flex-1 flex items-center justify-center gap-2 bg-teal-600 text-white px-5 py-2.5 rounded-md text-sm font-semibold hover:bg-teal-700 transition-colors cursor-pointer"
          >
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-upload-cloud-2-line" /></span>
            Subir documentos
          </button>
          <button
            onClick={() => { setSubmitted(false); setProjectCode(''); setAutores([{ id: 1, nombre: '', afiliacion: '', orcid: '' }]); setSede(''); setCarrera(''); }}
            className="whitespace-nowrap flex-1 px-5 py-2.5 text-sm font-medium text-slate-600 border border-slate-200 rounded-md hover:bg-slate-50 transition-colors cursor-pointer"
          >
            Presentar otro proyecto
          </button>
        </div>


        <div className="mt-6 bg-amber-50 border border-amber-200 rounded-lg p-3 max-w-sm w-full text-left">
          <p className="text-xs font-semibold text-amber-700 mb-1 flex items-center gap-1.5">
            <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-mail-check-line" /></span>
            Notificación por correo electrónico
          </p>
          <p className="text-xs text-amber-600">
            Cuando el Comité de Investigación e Interacción emita su dictamen, recibirá el resultado directamente en su correo electrónico.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-8 h-8 flex items-center justify-center bg-teal-600 rounded-md">
            <i className="ri-file-text-line text-white text-lg" />
          </div>
          <h2 className="text-2xl font-bold text-slate-800">Formulario de Recepción de Proyecto</h2>
        </div>
        <p className="text-slate-500 text-sm ml-11">Complete todos los campos para presentar su propuesta de investigación al Vicerrectorado / UGPP.</p>
      </div>

      <form
        data-readdy-form
        id="form-recepcion"
        onSubmit={handleSubmit}
        className="space-y-8"
      >
        {/* Datos del Investigador */}
        <section className="bg-white border border-slate-200 rounded-lg p-6">
          <h3 className="text-base font-semibold text-teal-700 mb-4 flex items-center gap-2">
            <span className="w-5 h-5 flex items-center justify-center"><i className="ri-user-line" /></span>
            Datos del Investigador Principal
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Nombre completo <span className="text-red-500">*</span></label>
              <input
                name="nombre_investigador"
                type="text"
                required
                defaultValue={currentUser?.nombre || ''}
                placeholder="Ej.: Dr. Juan Pérez"
                className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-slate-50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Correo electrónico <span className="text-red-500">*</span></label>
              <input
                name="email"
                type="email"
                required
                defaultValue={currentUser?.email || ''}
                readOnly={!!currentUser?.email}
                placeholder="correo@universidad.edu"
                className={`w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 ${currentUser?.email ? 'bg-teal-50 text-teal-800 font-medium cursor-not-allowed' : 'bg-slate-50'}`}
              />
              {currentUser?.email && (
                <p className="text-xs text-teal-600 mt-1 flex items-center gap-1">
                  <span className="w-3 h-3 flex items-center justify-center"><i className="ri-check-line" /></span>
                  Asociado a su cuenta registrada
                </p>
              )}
            </div>

            {/* Sede */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Sede <span className="text-red-500">*</span>
              </label>
              <select
                name="sede"
                required
                value={sede}
                onChange={e => { setSede(e.target.value); setCarrera(''); }}
                className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-slate-50 cursor-pointer"
              >
                <option value="">Seleccionar sede...</option>
                <option value="Oruro">Oruro</option>
                <option value="Cochabamba">Cochabamba</option>
              </select>
            </div>

            {/* Carrera */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Carrera <span className="text-red-500">*</span>
              </label>
              <select
                name="carrera"
                required
                value={carrera}
                onChange={e => setCarrera(e.target.value)}
                disabled={!sede}
                className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-slate-50 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="">{sede ? 'Seleccionar carrera...' : 'Primero seleccione una sede'}</option>
                {sede && CARRERAS_POR_SEDE[sede].map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
              {!sede && (
                <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                  <span className="w-3 h-3 flex items-center justify-center"><i className="ri-information-line" /></span>
                  Seleccione primero la sede para ver las carreras disponibles
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Teléfono de contacto</label>
              <input name="telefono" type="tel" placeholder="+00 000 000 0000" className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-slate-50" />
            </div>
          </div>
        </section>

        {/* Identificación del Proyecto */}
        <section className="bg-white border border-slate-200 rounded-lg p-6">
          <h3 className="text-base font-semibold text-teal-700 mb-4 flex items-center gap-2">
            <span className="w-5 h-5 flex items-center justify-center"><i className="ri-bookmark-line" /></span>
            Identificación del Proyecto
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Título del proyecto <span className="text-red-500">*</span></label>
              <input name="titulo" type="text" required placeholder="Título completo de la investigación" className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-slate-50" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Resumen ejecutivo <span className="text-red-500">*</span> <span className="text-slate-400 font-normal">({charCount.resumen}/500 Palabras)</span></label>
              <textarea name="resumen" required rows={4} onChange={handleResumenChange} placeholder="Descripción breve del proyecto, su alcance y relevancia científica..." className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-slate-50 resize-none" />
            </div>
            {/* Objetivos expandibles */}
            <div className="space-y-3">
              <p className="text-sm font-medium text-slate-700">Objetivos de la investigación <span className="text-red-500">*</span></p>

              {/* Objetivo General */}
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <button
                  type="button"
                  onClick={() => setObjGeneralOpen(p => !p)}
                  className="whitespace-nowrap w-full flex items-center justify-between px-4 py-3 bg-teal-50 hover:bg-teal-100 transition-colors cursor-pointer"
                >
                  <span className="flex items-center gap-2 text-sm font-semibold text-teal-700">
                    <span className="w-4 h-4 flex items-center justify-center"><i className="ri-focus-3-line" /></span>
                    Objetivo General
                    {objetivoGeneral.trim() && (
                      <span className="w-2 h-2 rounded-full bg-teal-500 inline-block" />
                    )}
                  </span>
                  <span className="w-4 h-4 flex items-center justify-center text-teal-600">
                    {objGeneralOpen ? <i className="ri-arrow-up-s-line" /> : <i className="ri-arrow-down-s-line" />}
                  </span>
                </button>
                {objGeneralOpen && (
                  <div className="p-4 bg-white">
                    <textarea
                      value={objetivoGeneral}
                      onChange={e => setObjetivoGeneral(e.target.value)}
                      required
                      rows={3}
                      maxLength={500}
                      placeholder="Describa el propósito principal de la investigación..."
                      className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-slate-50 resize-none"
                    />
                    <p className="text-xs text-slate-400 mt-1 text-right">{objetivoGeneral.length}/500</p>
                  </div>
                )}
              </div>

              {/* Objetivos Específicos */}
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <button
                  type="button"
                  onClick={() => setObjEspecificosOpen(p => !p)}
                  className="whitespace-nowrap w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors cursor-pointer"
                >
                  <span className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                    <span className="w-4 h-4 flex items-center justify-center"><i className="ri-list-check-2" /></span>
                    Objetivos Específicos
                    <span className="text-xs font-normal text-slate-400">({objetivosEspecificos.filter(o => o.trim()).length} agregado{objetivosEspecificos.filter(o => o.trim()).length !== 1 ? 's' : ''})</span>
                  </span>
                  <span className="w-4 h-4 flex items-center justify-center text-slate-500">
                    {objEspecificosOpen ? <i className="ri-arrow-up-s-line" /> : <i className="ri-arrow-down-s-line" />}
                  </span>
                </button>
                {objEspecificosOpen && (
                  <div className="p-4 bg-white space-y-3">
                    {objetivosEspecificos.map((obj, idx) => (
                      <div key={idx} className="flex items-start gap-2">
                        <span className="mt-2.5 w-5 h-5 flex items-center justify-center rounded-full bg-slate-200 text-slate-600 text-xs font-bold flex-shrink-0">
                          {idx + 1}
                        </span>
                        <textarea
                          value={obj}
                          onChange={e => updateObjetivoEspecifico(idx, e.target.value)}
                          rows={2}
                          maxLength={500}
                          placeholder={`Objetivo específico ${idx + 1}...`}
                          className="flex-1 border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-slate-50 resize-none"
                        />
                        {objetivosEspecificos.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeObjetivoEspecifico(idx)}
                            className="whitespace-nowrap mt-2 w-7 h-7 flex items-center justify-center text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors cursor-pointer flex-shrink-0"
                            title="Eliminar objetivo"
                          >
                            <i className="ri-delete-bin-line text-sm" />
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={addObjetivoEspecifico}
                      className="whitespace-nowrap flex items-center gap-1.5 text-xs font-semibold text-teal-600 border border-teal-300 bg-teal-50 rounded-md px-3 py-1.5 hover:bg-teal-100 transition-colors cursor-pointer mt-1"
                    >
                      <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-add-line" /></span>
                      Agregar objetivo específico
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Diseño y Metodología */}
        <section className="bg-white border border-slate-200 rounded-lg p-6">
          <h3 className="text-base font-semibold text-teal-700 mb-1 flex items-center gap-2">
            <span className="w-5 h-5 flex items-center justify-center"><i className="ri-draft-line" /></span>
            Diseño y Metodología
          </h3>
          <p className="text-xs text-slate-500 mb-4 ml-7">Describa cómo se llevará a cabo la investigación. Incluya los siguientes puntos en su respuesta:</p>

          {/* Guía visual */}
          <div className="mb-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {[
              { icon: 'ri-microscope-line', title: 'Tipo de estudio', desc: 'Ej.: descriptivo, experimental, cualitativo, cuantitativo, mixto.' },
              { icon: 'ri-settings-3-line', title: 'Metodología', desc: 'Procedimientos y técnicas que se usarán para recolectar y analizar datos.' },
              { icon: 'ri-group-line', title: 'Población y muestra', desc: 'A quiénes va dirigido el estudio y cómo se seleccionará la muestra.' },
              { icon: 'ri-tools-line', title: 'Instrumentos', desc: 'Encuestas, entrevistas, fichas de observación, equipos, software, etc.' },
              { icon: 'ri-bar-chart-line', title: 'Análisis de datos', desc: 'Cómo se procesará la información: estadística, análisis de contenido, etc.' },
              { icon: 'ri-map-pin-line', title: 'Lugar de ejecución', desc: 'Dónde se realizará la investigación (laboratorio, campo, institución, etc.).' },
            ].map(item => (
              <div key={item.title} className="flex items-start gap-2 bg-slate-50 border border-slate-100 rounded-lg p-3">
                <span className="w-7 h-7 flex items-center justify-center bg-teal-100 rounded-md flex-shrink-0 mt-0.5">
                  <i className={`${item.icon} text-teal-600 text-sm`} />
                </span>
                <div>
                  <p className="text-xs font-semibold text-slate-700">{item.title}</p>
                  <p className="text-xs text-slate-400 leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Descripción del diseño metodológico <span className="text-red-500">*</span>
                <span className="text-slate-400 font-normal ml-1">({charCount.diseno}/500)</span>
              </label>
              <textarea
                name="diseno"
                required
                rows={5}
                maxLength={500}
                onChange={e => updateChar('diseno', e.target.value)}
                placeholder="Ej.: La investigación es de tipo descriptivo-transversal con enfoque cuantitativo. Se aplicará una encuesta validada a una muestra de 120 estudiantes seleccionados por muestreo aleatorio estratificado. Los datos serán procesados con SPSS v.25 mediante análisis de frecuencias y correlación de Pearson. El estudio se realizará en las instalaciones de la carrera de Medicina, sede Oruro."
                className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-slate-50 resize-none"
              />
            </div>

            {/* Cronograma */}
            <div className="pt-2 border-t border-slate-100">
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Cronograma de actividades <span className="text-red-500">*</span>
                <span className="text-slate-400 font-normal ml-1">({charCount.cronograma}/500)</span>
              </label>
              <div className="mb-3 flex flex-wrap gap-2">
                {[
                  { icon: 'ri-search-line', label: 'Fase 1 — Revisión bibliográfica' },
                  { icon: 'ri-file-list-3-line', label: 'Fase 2 — Diseño de instrumentos' },
                  { icon: 'ri-database-2-line', label: 'Fase 3 — Recolección de datos' },
                  { icon: 'ri-pie-chart-line', label: 'Fase 4 — Análisis y resultados' },
                  { icon: 'ri-article-line', label: 'Fase 5 — Redacción y publicación' },
                ].map(f => (
                  <span key={f.label} className="inline-flex items-center gap-1 text-xs bg-amber-50 border border-amber-200 text-amber-700 rounded-full px-2.5 py-1">
                    <i className={`${f.icon} text-xs`} />
                    {f.label}
                  </span>
                ))}
              </div>
              <p className="text-xs text-slate-400 mb-2">Indique las fases del proyecto, las actividades de cada una y la duración estimada en semanas o meses.</p>
              <textarea
                name="cronograma"
                required
                rows={4}
                maxLength={500}
                onChange={e => updateChar('cronograma', e.target.value)}
                placeholder="Ej.: Mes 1-2: Revisión bibliográfica y diseño de instrumentos. Mes 3-4: Aplicación de encuestas y recolección de datos. Mes 5: Procesamiento y análisis estadístico. Mes 6: Redacción del informe final y preparación del artículo."
                className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-slate-50 resize-none"
              />
            </div>
          </div>
        </section>

        {/* ── AUTORES ── */}
        <section className="bg-white border border-slate-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-base font-semibold text-teal-700 flex items-center gap-2">
              <span className="w-5 h-5 flex items-center justify-center"><i className="ri-team-line" /></span>
              Autores del Proyecto
            </h3>
            <button
              type="button"
              onClick={addAutor}
              className="whitespace-nowrap flex items-center gap-1.5 text-xs font-semibold text-teal-600 border border-teal-300 bg-teal-50 rounded-md px-3 py-1.5 hover:bg-teal-100 transition-colors cursor-pointer"
            >
              <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-user-add-line" /></span>
              Agregar autor
            </button>
          </div>

          <p className="text-xs text-slate-500 mb-4">
            Registre cada autor individualmente. El primer autor será considerado el investigador responsable.
            El ORCID es opcional pero recomendado — se ingresa en formato URL: <span className="font-mono text-slate-600">https://orcid.org/0000-0001-8702-3405</span>
          </p>

          <div className="space-y-4">
            {autores.map((autor, index) => (
              <div
                key={autor.id}
                className="border border-slate-200 rounded-lg p-4 bg-slate-50 relative"
              >
                {/* Badge de posición */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${index === 0 ? 'bg-teal-600 text-white' : 'bg-slate-300 text-slate-700'}`}>
                      {index + 1}
                    </span>
                    <span className="text-xs font-semibold text-slate-600">
                      {index === 0 ? 'Autor responsable / Investigador principal' : `Co-autor ${index}`}
                    </span>
                    {index === 0 && (
                      <span className="bg-teal-100 text-teal-700 text-xs font-semibold px-2 py-0.5 rounded-full">Principal</span>
                    )}
                  </div>
                  {autores.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeAutor(autor.id)}
                      className="whitespace-nowrap w-6 h-6 flex items-center justify-center text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors cursor-pointer"
                      title="Eliminar autor"
                    >
                      <i className="ri-delete-bin-line text-sm" />
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {/* Nombre */}
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">
                      Nombre completo <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={autor.nombre}
                      onChange={e => updateAutor(autor.id, 'nombre', e.target.value)}
                      placeholder="Ej.: Dra. María López"
                      className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white"
                    />
                  </div>

                  {/* Afiliación */}
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">
                      Afiliación principal <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={autor.afiliacion}
                      onChange={e => updateAutor(autor.id, 'afiliacion', e.target.value)}
                      placeholder="Ej.: Universidad Nacional, Facultad de Ciencias"
                      className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white"
                    />
                  </div>

                  {/* ORCID */}
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1 flex items-center gap-1">
                      ORCID
                      <a
                        href="https://orcid.org"
                        target="_blank"
                        rel="noopener noreferrer nofollow"
                        className="text-teal-500 hover:text-teal-700 cursor-pointer"
                        title="¿Qué es ORCID?"
                      >
                        <i className="ri-information-line text-xs" />
                      </a>
                    </label>
                    <div className="flex items-center border border-slate-200 rounded-md bg-white focus-within:ring-2 focus-within:ring-teal-500 overflow-hidden">
                      <span className="px-2 py-2 text-xs font-mono text-slate-400 bg-slate-100 border-r border-slate-200 whitespace-nowrap select-none">
                        https://orcid.org/
                      </span>
                      <input
                        type="text"
                        value={autor.orcid}
                        onChange={e => updateAutor(autor.id, 'orcid', formatOrcid(e.target.value))}
                        placeholder="0000-0000-0000-0000"
                        maxLength={19}
                        className="flex-1 px-2 py-2 text-sm font-mono focus:outline-none bg-white tracking-wider min-w-0"
                      />
                    </div>
                    {autor.orcid && autor.orcid.length === 19 && (
                      <a
                        href={`https://orcid.org/${autor.orcid}`}
                        target="_blank"
                        rel="noopener noreferrer nofollow"
                        className="text-xs text-teal-600 hover:underline mt-1 inline-flex items-center gap-1 cursor-pointer"
                      >
                        <i className="ri-external-link-line text-xs" />
                        https://orcid.org/{autor.orcid}
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Separador + sección financiamiento */}
          <div className="mt-6 pt-5 border-t border-slate-200 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Presupuesto estimado (opcional)</label>
              <div className="flex items-center border border-slate-200 rounded-md bg-slate-50 focus-within:ring-2 focus-within:ring-teal-500 overflow-hidden">
                <span className="px-3 py-2 text-sm font-semibold text-slate-500 bg-slate-100 border-r border-slate-200 whitespace-nowrap select-none">Bs.</span>
                <input name="presupuesto" type="number" min="0" step="0.01" placeholder="Ej.: 35,000" className="flex-1 px-3 py-2 text-sm focus:outline-none bg-transparent" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Fuente de financiamiento</label>
              <select name="financiamiento" className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-slate-50 cursor-pointer">
                <option value="">Seleccionar...</option>
                <option value="Institucional">Institucional</option>
                <option value="Externo - Público">Externo - Público</option>
                <option value="Externo - Privado">Externo - Privado</option>
                <option value="Autofinanciado">Autofinanciado</option>
                <option value="Mixto">Mixto</option>
              </select>
            </div>
          </div>
        </section>

        {/* Consideraciones Éticas */}
        <section className="bg-white border border-slate-200 rounded-lg p-6">
          <h3 className="text-base font-semibold text-teal-700 mb-4 flex items-center gap-2">
            <span className="w-5 h-5 flex items-center justify-center"><i className="ri-shield-check-line" /></span>
            Consideraciones Éticas
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">¿El proyecto requiere evaluación del Comité de Ética? <span className="text-red-500">*</span></label>
              <div className="flex gap-4">
                {['Sí', 'No', 'No estoy seguro/a'].map((opt) => (
                  <label key={opt} className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                    <input type="radio" name="necesidad_etica" value={opt} required className="accent-teal-600" />
                    {opt}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Justificación ética (si aplica)</label>
              <textarea name="justificacion_etica" rows={3} maxLength={500} placeholder="Describa los riesgos, participantes vulnerables, manejo de datos personales..." className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-slate-50 resize-none" />
            </div>
          </div>
        </section>

        {/* Plan de Publicación */}
        <section className="bg-white border border-slate-200 rounded-lg p-6">
          <h3 className="text-base font-semibold text-teal-700 mb-1 flex items-center gap-2">
            <span className="w-5 h-5 flex items-center justify-center"><i className="ri-book-open-line" /></span>
            Plan de Publicación
          </h3>
          <p className="text-xs text-slate-500 mb-4 ml-7">Indique cómo y dónde planea difundir los resultados de su investigación.</p>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Plan de publicación y difusión <span className="text-red-500">*</span> <span className="text-slate-400 font-normal">({charCount.publicacion}/500)</span></label>

              {/* Guía de publicación */}
              <div className="mb-3 grid grid-cols-1 sm:grid-cols-3 gap-2">
                {[
                  { icon: 'ri-newspaper-line', title: '¿Dónde publicar?', desc: 'Nombre de la revista científica, congreso o evento académico al que se enviará el artículo.' },
                  { icon: 'ri-lock-unlock-line', title: 'Tipo de acceso', desc: 'Acceso abierto (open access) o cerrado. Si es indexada, mencione el índice (Scopus, Latindex, SciELO, etc.).' },
                  { icon: 'ri-calendar-check-line', title: 'Fecha estimada', desc: 'Mes y año aproximado en que planea enviar el manuscrito o presentar los resultados.' },
                ].map(item => (
                  <div key={item.title} className="flex items-start gap-2 bg-slate-50 border border-slate-100 rounded-lg p-3">
                    <span className="w-7 h-7 flex items-center justify-center bg-teal-100 rounded-md flex-shrink-0 mt-0.5">
                      <i className={`${item.icon} text-teal-600 text-sm`} />
                    </span>
                    <div>
                      <p className="text-xs font-semibold text-slate-700">{item.title}</p>
                      <p className="text-xs text-slate-400 leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <textarea name="plan_publicacion" required rows={3} maxLength={500} onChange={e => updateChar('publicacion', e.target.value)} placeholder="Ej.: Se planea publicar los resultados en la Revista Boliviana de Salud (indexada en Latindex) en acceso abierto, aproximadamente en el mes 7 del proyecto. Además, se presentará un resumen en el Congreso Nacional de Investigación en Salud previsto para noviembre de 2026." className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-slate-50 resize-none" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Área del conocimiento <span className="text-red-500">*</span></label>
                <select name="area_conocimiento" required className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-slate-50 cursor-pointer">
                  <option value="">Seleccionar área...</option>
                  <option value="Ciencias de la Salud">Ciencias de la Salud</option>
                  <option value="Ciencias Exactas y Naturales">Ciencias Exactas y Naturales</option>
                  <option value="Ciencias Sociales">Ciencias Sociales</option>
                  <option value="Humanidades">Humanidades</option>
                  <option value="Ingeniería y Tecnología">Ingeniería y Tecnología</option>
                  <option value="Ciencias Económicas y Administrativas">Ciencias Económicas y Administrativas</option>
                  <option value="Educación">Educación</option>
                  <option value="Arte y Diseño">Arte y Diseño</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Fecha estimada de inicio <span className="text-red-500">*</span></label>
                <input name="fecha_inicio" type="date" required className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-slate-50 cursor-pointer" />
              </div>
            </div>
          </div>
        </section>

        <div className="flex justify-end gap-3">
          <button type="reset" className="whitespace-nowrap px-5 py-2.5 text-sm font-medium text-slate-600 border border-slate-200 rounded-md hover:bg-slate-50 transition-colors cursor-pointer">
            Limpiar formulario
          </button>
          <button
            type="submit"
            disabled={loading}
            className="whitespace-nowrap px-6 py-2.5 text-sm font-medium text-white bg-teal-600 rounded-md hover:bg-teal-700 transition-colors cursor-pointer disabled:opacity-60 flex items-center gap-2"
          >
            {loading ? (
              <><span className="w-4 h-4 flex items-center justify-center"><i className="ri-loader-4-line animate-spin" /></span> Enviando...</>
            ) : (
              <><span className="w-4 h-4 flex items-center justify-center"><i className="ri-send-plane-line" /></span> Presentar Proyecto</>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
